/**
 * Shared Cash Position Summary types + row mappers (DCP-143 / DCP-144).
 *
 * The heavy per-facility aggregation lives entirely in SQL: `get_cash_position_summary`
 * (DCP-143) and the multi-period wrapper `get_cash_position_summary_multi_period`
 * (DCP-144, consumed by {@link useCashPositionSummaryMultiPeriod}). This module only
 * holds the display shape, the PostgREST-string→number coercion, and the row→shape
 * mapper reused by the hook. No hook lives here — kept as the shared module so the
 * query-key and helpers have one home.
 */

// Definition moved to `@/lib/query-keys` alongside the other cache keys, so code
// that only needs a key does not load this module. Re-exported for the existing
// import sites; new code should import from `@/lib/query-keys` directly.
export { CASH_POSITION_SUMMARIES_KEY } from '@/lib/query-keys'

/**
 * Fully-computed Cash Position Summary for one column (facility or pack subtotal
 * or the combined total). Every field is a final number ready for display — no
 * math in the UI. All aggregation happens in SQL; the hook only maps rows onto
 * this shape.
 *
 * Outflow fields are signed (negative) because projection `amount` is signed
 * (DCP-250: inflow positive, outflow negative); the SQL never flips signs.
 */
export interface CashPositionSummary {
  /** Latest manual balance date for the column (ISO YYYY-MM-DD) or null. */
  asOfDate: string | null
  /** Manual bank balance total. */
  bankBalance: number
  /** Outstanding (issued) checks, stored negative. */
  outstandingChecks: number
  /** bankBalance + outstandingChecks (signed). */
  bookBalance: number
  /** Σ active Sage `intacct_balance` for the column's Sage location(s). */
  intacctBalance: number
  /** bookBalance − intacctBalance. */
  bookVsIntacct: number
  /** Σ DEPOSIT projections (signed, positive). */
  projectedInflows: number
  /** Per-category signed outflow sums (each negative). */
  outflows: {
    acctsPayable: number
    payroll: number
    creditCard: number
    mgmtFee: number
    distribution: number
    rent: number
    escrow: number
    capEx: number
    chargebacks: number
    /** = Σ UNSPECIFIED (no pack threshold — facility is treated as packless). */
    minimumDailyBankBalance: number
  }
  /** Sum of all `outflows` fields. */
  projectedOutflows: number
  /** Σ INTER CO LOAN projections (signed). */
  interCoLoan: number
  /** Σ LINE OF CREDIT projections (signed). */
  lineOfCredit: number
  /** interCoLoan + lineOfCredit. */
  interCoLoanLocProjections: number
  /** projectedInflows + projectedOutflows + interCoLoanLocProjections. */
  netProjectedCashFlow: number
  /** bookBalance + netProjectedCashFlow. */
  expectedEowBookBalance: number
  /**
   * LOC borrowing base for the column (DCP-147). Pack-level: a number for a
   * standalone facility or a pack subtotal / the Total, NULL for a pack-member
   * facility column or a reference with no LOC line (→ rendered as "-"). Null is
   * meaningful (no base), distinct from a $0 base, so it is preserved end-to-end.
   */
  locBorrowingBase: number | null
  /**
   * LOC usage for the column (DCP-147). Line-level (starting baseline + Σ LINE OF
   * CREDIT tx) at standalone / pack / Total; a pack-member column carries its own
   * transaction flow only (display-only). Always a number.
   */
  locUsage: number
  /**
   * Projections each row sums, by the amounts' predicate (so a cell netting to $0
   * still counts) — gates the "Manage projections" icon.
   */
  projectionCounts: ProjectionCounts
}

export interface ProjectionCounts {
  projectedInflows: number
  acctsPayable: number
  payroll: number
  creditCard: number
  mgmtFee: number
  distribution: number
  rent: number
  escrow: number
  capEx: number
  chargebacks: number
  minimumDailyBankBalance: number
  /** Sum of the ten outflow counts. */
  projectedOutflows: number
  interCoLoan: number
  lineOfCredit: number
}

/**
 * Numeric fields shared by both Cash Position RPCs. PostgREST surfaces NUMERIC
 * columns as strings, so every numeric is typed `number | string | null` and
 * coerced with {@link num} on the way in. The multi-period RPC returns a superset
 * of this (period_index / row_type / pack_id / …); {@link mapRow} reads only these fields.
 */
export interface CashPositionRpcRow {
  facility_id: string | null
  facility_name: string
  as_of_date: string | null
  bank_balance: number | string | null
  outstanding_checks: number | string | null
  book_balance: number | string | null
  intacct_balance: number | string | null
  book_vs_intacct: number | string | null
  projected_inflows: number | string | null
  accts_payable: number | string | null
  payroll: number | string | null
  credit_card: number | string | null
  mgmt_fee: number | string | null
  distribution: number | string | null
  rent: number | string | null
  escrow: number | string | null
  cap_ex: number | string | null
  chargebacks: number | string | null
  minimum_daily_bank_balance: number | string | null
  projected_outflows: number | string | null
  inter_co_loan: number | string | null
  line_of_credit: number | string | null
  inter_co_loan_loc_projections: number | string | null
  net_projected_cash_flow: number | string | null
  expected_eow_book_balance: number | string | null
  // DCP-147 LOC snapshot. `loc_borrowing_base` is genuinely nullable (no line /
  // pack member) — see the null-check in {@link mapRow}.
  loc_borrowing_base: number | string | null
  loc_usage: number | string | null
  // Optional: a database without migration 20261016000000 omits them; they read
  // as 0, hiding the Manage projections icon rather than breaking the report.
  projected_inflows_count?: number | string | null
  accts_payable_count?: number | string | null
  payroll_count?: number | string | null
  credit_card_count?: number | string | null
  mgmt_fee_count?: number | string | null
  distribution_count?: number | string | null
  rent_count?: number | string | null
  escrow_count?: number | string | null
  cap_ex_count?: number | string | null
  chargebacks_count?: number | string | null
  minimum_daily_bank_balance_count?: number | string | null
  projected_outflows_count?: number | string | null
  inter_co_loan_count?: number | string | null
  line_of_credit_count?: number | string | null
}

/**
 * Coerce a PostgREST numeric (which may arrive as a string) to a finite number.
 * Null / undefined / non-finite inputs collapse to 0 so the grid never renders NaN.
 */
export function num(value: number | string | null | undefined): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * Map one RPC row onto the {@link CashPositionSummary} display shape, coercing
 * every numeric and copying `as_of_date`. The multi-period RPC row is a superset
 * of {@link CashPositionRpcRow}, so this mapper reads only the shared numeric fields.
 *
 * @param row - a Cash Position RPC row
 */
export function mapRow(row: CashPositionRpcRow): CashPositionSummary {
  return {
    asOfDate: row.as_of_date,
    bankBalance: num(row.bank_balance),
    outstandingChecks: num(row.outstanding_checks),
    bookBalance: num(row.book_balance),
    intacctBalance: num(row.intacct_balance),
    bookVsIntacct: num(row.book_vs_intacct),
    projectedInflows: num(row.projected_inflows),
    outflows: {
      acctsPayable: num(row.accts_payable),
      payroll: num(row.payroll),
      creditCard: num(row.credit_card),
      mgmtFee: num(row.mgmt_fee),
      distribution: num(row.distribution),
      rent: num(row.rent),
      escrow: num(row.escrow),
      capEx: num(row.cap_ex),
      chargebacks: num(row.chargebacks),
      minimumDailyBankBalance: num(row.minimum_daily_bank_balance),
    },
    projectedOutflows: num(row.projected_outflows),
    interCoLoan: num(row.inter_co_loan),
    lineOfCredit: num(row.line_of_credit),
    interCoLoanLocProjections: num(row.inter_co_loan_loc_projections),
    netProjectedCashFlow: num(row.net_projected_cash_flow),
    expectedEowBookBalance: num(row.expected_eow_book_balance),
    // NULL-check BEFORE coercion: `num()` collapses null → 0, which would wrongly
    // render a "no LOC line" column as "$0" instead of "-". A real null base
    // (pack member / no line) must stay null.
    locBorrowingBase: row.loc_borrowing_base == null ? null : num(row.loc_borrowing_base),
    locUsage: num(row.loc_usage),
    projectionCounts: {
      projectedInflows: num(row.projected_inflows_count),
      acctsPayable: num(row.accts_payable_count),
      payroll: num(row.payroll_count),
      creditCard: num(row.credit_card_count),
      mgmtFee: num(row.mgmt_fee_count),
      distribution: num(row.distribution_count),
      rent: num(row.rent_count),
      escrow: num(row.escrow_count),
      capEx: num(row.cap_ex_count),
      chargebacks: num(row.chargebacks_count),
      minimumDailyBankBalance: num(row.minimum_daily_bank_balance_count),
      projectedOutflows: num(row.projected_outflows_count),
      interCoLoan: num(row.inter_co_loan_count),
      lineOfCredit: num(row.line_of_credit_count),
    },
  }
}

/**
 * All-zero summary used when the RPC returns no total row (e.g. an org with no
 * facilities, or filters matching nothing). Keeps the grid well-formed.
 */
export function emptyTotal(): CashPositionSummary {
  return {
    asOfDate: null,
    bankBalance: 0,
    outstandingChecks: 0,
    bookBalance: 0,
    intacctBalance: 0,
    bookVsIntacct: 0,
    projectedInflows: 0,
    outflows: {
      acctsPayable: 0,
      payroll: 0,
      creditCard: 0,
      mgmtFee: 0,
      distribution: 0,
      rent: 0,
      escrow: 0,
      capEx: 0,
      chargebacks: 0,
      minimumDailyBankBalance: 0,
    },
    projectedOutflows: 0,
    interCoLoan: 0,
    lineOfCredit: 0,
    interCoLoanLocProjections: 0,
    netProjectedCashFlow: 0,
    expectedEowBookBalance: 0,
    // No facilities / no LOC line → base "-" (null), usage 0 (DCP-147).
    locBorrowingBase: null,
    locUsage: 0,
    projectionCounts: {
      projectedInflows: 0,
      acctsPayable: 0,
      payroll: 0,
      creditCard: 0,
      mgmtFee: 0,
      distribution: 0,
      rent: 0,
      escrow: 0,
      capEx: 0,
      chargebacks: 0,
      minimumDailyBankBalance: 0,
      projectedOutflows: 0,
      interCoLoan: 0,
      lineOfCredit: 0,
    },
  }
}
